export type UserRoleType =
  | ''
  | 'ADMIN'
  | 'USER'
  | 'BLOCKED'
  | 'BLOCKED_COMMUNITY'
  | 'BUBBLEVERSE_CREATOR'
  | 'BUBBLETAP_CREATOR'
  | 'BUBBLEUP_USER';

/**
 * 크리에이터 등급 코드 (#2039). NONE 은 일반 유저로 뱃지를 노출하지 않는다.
 * NONE 일반 · STARDUST 별가루 · RISING_STAR 샛별 · STARKEEPER 별지기
 *
 * 서버가 나중에 중간 등급을 끼워 넣을 수 있어 숫자로 다루지 않는다. 서열이 필요하면
 * CREATOR_GRADE_ORDER 의 인덱스를 쓰고, 모르는 코드가 오면 NONE 으로 떨어뜨린다.
 */
export type CreatorGradeCode = 'NONE' | 'STARDUST' | 'RISING_STAR' | 'STARKEEPER';

/** 낮은 등급부터의 서열. 서버에 등급이 추가되면 이 배열만 고치면 된다 */
export const CREATOR_GRADE_ORDER = ['NONE', 'STARDUST', 'RISING_STAR', 'STARKEEPER'] as const;

export interface CreatorGradeSource {
  creatorGradeCode?: CreatorGradeCode | null;
  /** 현재 등급이 된 시점. 등급을 받은 적이 없으면 null */
  creatorGradeChangedAt?: string | null;
  /**
   * 등급제 이전 필드. 서버는 항상 `isCreator === (creatorGradeCode !== 'NONE')` 로 맞춰 내려주므로
   * creatorGradeCode 가 없는 낡은 응답에서만 1단계 폴백에 쓴다.
   */
  isCreator?: boolean | null;
}

const isKnownGradeCode = (code: string): code is CreatorGradeCode =>
  (CREATOR_GRADE_ORDER as readonly string[]).includes(code);

/**
 * 유저 응답에서 등급 코드를 판정한다. 소스를 여러 개 받아 앞에서부터 등급 코드를 가진 첫 소스를 쓴다.
 *
 * 응답마다 등급이 실리는 범위가 달라 소스를 하나만 믿으면 안 된다(2026-09-14 dev 실측).
 * - 애셋의 작성자 객체 `CompactAiUserDto`, `/my/profile`, 공개 유저 응답 모두 `creatorGradeCode` 를 준다
 * - `creatorGradeChangedAt` 은 공개 응답에 없다. 전환 시점이 필요하면 `/my/profile` 을 쓴다
 *
 * 애셋 지면은 [애셋의 작성자 객체, 작가 프로필] 순으로 넘긴다. 작성자 객체가 이미 등급을 들고
 * 있어 프로필 조회를 기다리지 않고 바로 정확한 뱃지를 그린다.
 *
 * 등급 필드가 아예 없는 응답만 isCreator 로 폴백하고, 모르는 코드는 NONE 으로 처리해 뱃지를 감춘다.
 */
export const resolveCreatorGradeCode = (...sources: (CreatorGradeSource | null | undefined)[]): CreatorGradeCode => {
  for (const source of sources) {
    if (source?.creatorGradeCode != null) {
      return isKnownGradeCode(source.creatorGradeCode) ? source.creatorGradeCode : 'NONE';
    }
  }
  for (const source of sources) {
    if (source?.isCreator != null) return source.isCreator ? 'STARDUST' : 'NONE';
  }
  return 'NONE';
};

export interface MyProfileResponseDto {
  id: string;
  externalId: string;
  username: string;
  avatar?: string /** @example 88369f19-244f-403c-9665-07e17d4951ce_1.jpg */;
  description: string /** @example description for the user */;
  email: string /** @example charming.writer@book.com */;
  verified: boolean /** @example true */;
  identityVerifiedAt: string;
  bankAccountVerifiedAt: string;
  isCreator: boolean;
  /** 크리에이터 등급 코드. 등급제 미지원 응답에서는 빠질 수 있어 옵셔널 (#2039) */
  creatorGradeCode?: CreatorGradeCode;
  /** 현재 등급이 된 시점. 등급을 받은 적이 없으면 null (#2039) */
  creatorGradeChangedAt?: string | null;
  creatorVerifiedAt: string | null;
  revenueShareTermsAgreedAt: string | null;
  isAdult: boolean;
  countryCode: string /** @example KR */;
  displayLanguageCode: string /** @example en */;
  storyLanguageCode: string /** @example en */;
  statistics?: UserStatisticsDto;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  role: UserRoleDto;
  provider: UserProviderDto;
  blockedBy: string;
  blockedAt: string;
  hasPassword: boolean;
  autoSaveInterval: number;
  autoSaveEnabled: boolean;
  writername: string;
  composedAvatar?: string;
  decoratorUrl?: string;
}

export interface UserStatisticsDto {
  id: string;
  userId: string;
  numFollowers: number;
  numFollowing: number;
  numTaps: string;
  numLikes: number;
  numComments: number;
  numStars: number;
  numStarsParticipants: number;
}
export interface UserRoleDto {
  code: UserRoleType /** @example USER, ADMIN */;
  name: string;
}

export interface UserProviderDto {
  /** @example GOOGLE */
  code: string;
}
